import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Chip, 
  Grid,
  Tabs,
  Tab,
  Button,
  Rating,
  Avatar,
  Divider
} from '@mui/material';
import { 
  ArrowBack, 
  CalendarToday, 
  LocationOn, 
  Star,
  CheckCircle,
  Schedule,
  Cancel,
  Assignment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';

interface Campaign {
  id: string;
  title: string;
  company: string;
  status: '신청' | '선정' | '등록' | '종료' | '거절';
  appliedDate: string;
  deadline: string;
  location: string;
  reward: string;
  rating: number;
  image: string;
}

const mockCampaigns: Campaign[] = [
  {
    id: '1',
    title: '서울 강남구 맛집 탐방',
    company: '강남구청',
    status: '신청',
    appliedDate: '2024-01-15',
    deadline: '2024-02-28',
    location: '서울 강남구',
    reward: '500P',
    rating: 4.8,
    image: '/api/placeholder/300/200'
  },
  {
    id: '2',
    title: '부산 해운대 카페 투어',
    company: '해운대구청',
    status: '선정',
    appliedDate: '2024-01-10',
    deadline: '2024-03-15',
    location: '부산 해운대구',
    reward: '400P',
    rating: 4.6,
    image: '/api/placeholder/300/200'
  },
  {
    id: '3',
    title: '제주도 숙박 체험',
    company: '제주관광협회',
    status: '등록',
    appliedDate: '2024-01-05',
    deadline: '2024-04-30',
    location: '제주도',
    reward: '1000P',
    rating: 4.9,
    image: '/api/placeholder/300/200'
  }
];

const getStatusColor = (status: string) => {
  switch (status) {
    case '신청': return 'primary';
    case '선정': return 'success';
    case '등록': return 'warning';
    case '종료': return 'default';
    case '거절': return 'error';
    default: return 'default';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case '신청': return <Schedule />;
    case '선정': return <CheckCircle />;
    case '등록': return <Assignment />;
    case '종료': return <CheckCircle />;
    case '거절': return <Cancel />;
    default: return <Schedule />;
  }
};

const ReviewerCampaignApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate('/mypage/reviewer');
  };

  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaign/${campaignId}`);
  };

  const filteredCampaigns = mockCampaigns.filter(campaign => {
    if (tabValue === 0) return campaign.status === '신청';
    if (tabValue === 1) return campaign.status === '선정';
    if (tabValue === 2) return campaign.status === '등록';
    if (tabValue === 3) return campaign.status === '종료';
    return true;
  });

  return (
    <>
      <Header showIcons={true} />
      <Container maxWidth="md" sx={{ pb: 8, pt: 0 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            뒤로
          </Button>
          <Typography variant="h5" component="h1">
            캠페인 신청내역
          </Typography>
        </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              px: 2,
              py: 1.5
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }
          }}
        >
          <Tab label="신청" />
          <Tab label="선정" />
          <Tab label="등록" />
          <Tab label="종료" />
        </Tabs>
      </Box>

      {/* Campaign List */}
      <Box>
        {filteredCampaigns.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              신청한 캠페인이 없습니다
            </Typography>
            <Typography variant="body2" color="text.secondary">
              새로운 캠페인에 신청해보세요!
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2 }}
              onClick={() => navigate('/category')}
            >
              캠페인 둘러보기
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredCampaigns.map((campaign) => (
              <Grid item xs={12} key={campaign.id}>
                <Card 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleCampaignClick(campaign.id)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Avatar
                        src={campaign.image}
                        variant="rounded"
                        sx={{ width: 100, height: 100, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography 
                            variant="h6" 
                            component="h2" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '1.1rem',
                              lineHeight: 1.3,
                              mb: 1
                            }}
                          >
                            {campaign.title}
                          </Typography>
                          <Chip
                            icon={getStatusIcon(campaign.status)}
                            label={campaign.status}
                            color={getStatusColor(campaign.status) as any}
                            size="small"
                            sx={{ 
                              fontSize: '0.8rem',
                              fontWeight: 500,
                              height: '24px'
                            }}
                          />
                        </Box>
                        
                        <Typography 
                          variant="body1" 
                          color="text.secondary" 
                          sx={{ 
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            mb: 2
                          }}
                        >
                          {campaign.company}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn fontSize="small" color="action" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem' }}
                            >
                              {campaign.location}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday fontSize="small" color="action" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem' }}
                            >
                              신청일: {campaign.appliedDate} | 마감일: {campaign.deadline}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Rating value={campaign.rating} readOnly size="small" />
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: '0.9rem', fontWeight: 500 }}
                            >
                              {campaign.rating}
                            </Typography>
                          </Box>
                          <Typography 
                            variant="h6" 
                            color="primary" 
                            sx={{ 
                              fontWeight: 700,
                              fontSize: '1.2rem'
                            }}
                          >
                            {campaign.reward}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      </Container>
    </>
  );
};

export default ReviewerCampaignApplicationsPage; 